import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { getImageUrl } from '../utils/imageUrl';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { log } from '../utils/logger';
import { usePageMeta } from '../utils/seo';
import './News.css';

const News = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  usePageMeta({
    title: id && news?.title ? news.title : 'Notícias',
    description: id ? undefined : 'Notícias, artigos e informativos do escritório Navarro & Vieira Advogados.'
  });

  useEffect(() => {
    // Sempre buscar a lista de notícias
    fetchNewsList();
    
    // Se há um ID, buscar a notícia específica
    if (id) {
      fetchSingleNews(id);
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchSingleNews = async (newsId) => {
    try {
      setError(null);
      const response = await api.get(`/news/${newsId}`);
      setNews(response.data.news);
    } catch (error) {
      log.error('Erro ao buscar notícia:', error);
      if (error.response?.status === 404) {
        setError('Notícia não encontrada');
      } else {
        setError('Erro ao carregar notícia');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchNewsList = async () => {
    try {
      const response = await api.get('/news');
      setNewsList(response.data.news || []);
    } catch (error) {
      log.error('Erro ao buscar notícias:', error);
    }
  };

  const handleNewsClick = (newsId) => {
    navigate(`/noticias/${newsId}`);
  };

  if (loading && id) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="news-page">
      <div className="news-container">
        {/* Sidebar com lista de notícias */}
        <aside className="news-sidebar">
          <h2>Notícias</h2>
          {newsList.length > 0 ? (
            <div className="news-sidebar-list">
              {newsList.map((item) => (
                <div
                  key={item.id}
                  className={`news-sidebar-item ${id && parseInt(id) === item.id ? 'active' : ''}`}
                  onClick={() => handleNewsClick(item.id)}
                >
                  {item.image_url && (
                    <div
                      className="news-sidebar-image-wrap"
                      style={{ backgroundImage: `url(${getImageUrl(item.image_url)})` }}
                    >
                      <img
                        src={getImageUrl(item.image_url)}
                        alt={item.title}
                        className="news-sidebar-image-on-top"
                      />
                    </div>
                  )}
                  <div className="news-sidebar-content">
                    <h4>{item.title}</h4>
                    <p className="news-sidebar-date">
                      {new Date(item.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-news-sidebar">Nenhuma notícia disponível</p>
          )}
        </aside>

        {/* Conteúdo principal */}
        <main className="news-main">
          {id && error ? (
            <div className="error-message">
              <h2>{error}</h2>
              <Link to="/noticias" className="back-link">← Voltar para notícias</Link>
            </div>
          ) : id && news ? (
            <article className="news-article">
              {news.image_url && (
                <div
                  className="news-article-image-wrap"
                  style={{ backgroundImage: `url(${getImageUrl(news.image_url)})` }}
                >
                  <img
                    src={getImageUrl(news.image_url)}
                    alt={news.title}
                    className="news-article-image-on-top"
                  />
                </div>
              )}
              <h1>{news.title}</h1>
              <div className="news-meta">
                <span>Por {news.author_name || 'Admin'}</span>
                <span>
                  {new Date(news.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
              <div
                className="news-content"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(news.content || '') }}
              />
            </article>
          ) : (
            <div className="news-welcome">
              <h1>NOTÍCIAS E ATUALIZAÇÕES</h1>
              <p>Selecione uma notícia na lista ao lado para ler</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default News;

